import React from "react";
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { BASE_URL } from "../services/config";
import { Button, Input, Textarea, Link, addToast } from "@heroui/react";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import heroBackground from '../assets/images/contact-banner.png';
import api from "../services/api";
import StoresMap from '../components/StoresMap';

export const ContactsPage: React.FC = () => {
  const location = useLocation();
  const [formData, setFormData] = React.useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitSuccess, setSubmitSuccess] = React.useState(false);
  const [stores, setStores] = React.useState<any[] | null>(null);
  const [storesError, setStoresError] = React.useState<string | null>(null);
  const [selectedStoreId, setSelectedStoreId] = React.useState<number | null>(null);

  // Yandex Maps API key (read from env). Developers should set VITE_YANDEX_MAPS_API_KEY in .env
  const yandexApiKey = (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env.VITE_YANDEX_MAPS_API_KEY) ? (import.meta as any).env.VITE_YANDEX_MAPS_API_KEY : undefined;

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get('/stores/');
        const data = resp?.data;
        if (cancelled) return;
        if (!data) {
          setStores([]);
          return;
        }
        // data may be paginated { results: [...] } or direct array
        if (Array.isArray(data)) setStores(data);
        else if (Array.isArray(data.results)) setStores(data.results);
        else setStores([]);
      } catch (err: any) {
        console.error('Stores fetch error', err);
        if (!cancelled) setStoresError(err?.response?.data?.message || 'Ошибка при загрузке торговых точек');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // basic validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      addToast({ title: "Ошибка", description: "Пожалуйста, заполните имя, email и сообщение.", color: "danger" });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: any = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
      };
      if (formData.phone && formData.phone.trim()) payload.phone = formData.phone.trim();
      if (formData.subject && formData.subject.trim()) payload.subject = formData.subject.trim();

      await api.post('/contact/', payload, { headers: { 'Content-Type': 'application/json' } });

      addToast({ title: "Сообщение отправлено", description: "Спасибо! Мы свяжемся с вами в ближайшее время.", color: "success" });
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
      setSubmitSuccess(true);

      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (err: any) {
      console.error('Contact submit error', err);
      const msg = err?.response?.data?.message || err?.message || 'Неизвестная ошибка';
      addToast({ title: "Ошибка отправки", description: msg, color: "danger" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Контакты — SAFONOVA</title>
        <meta name="description" content="Свяжитесь с SAFONOVA — контактная информация, форма обратной связи и список торговых точек. Мы ответим в течение 24 часов." />
        <link rel="canonical" href={`${BASE_URL || ''}${location?.pathname || '/contacts'}`} />
        <meta property="og:title" content="Контакты — SAFONOVA" />
        <meta property="og:description" content="Свяжитесь с SAFONOVA — контактная информация, форма обратной связи и список торговых точек. Мы ответим в течение 24 часов." />
        <meta property="og:url" content={`${BASE_URL || ''}${location?.pathname || '/contacts'}`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${BASE_URL || ''}/og-default.png`} />
      </Helmet>
      {/* Hero Section */}
      <section 
        // className="py-24 bg-content2"
        className="relative flex items-center py-32 md:py-40"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.10)), url('${heroBackground}')`,
          backgroundPosition: "center 60%",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat"
        }}>
        <div className="container mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white text-shadow">
              Наши <span className="font-medium">контакты</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8 max-w-xl text-shadow">
              Свяжитесь с нами любым удобным для вас способом или оставьте сообщение через форму обратной связи
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
              className="order-2 lg:order-1"
            >
              <h2 className="text-2xl md:text-3xl font-light mb-6">Форма <span className="font-medium">обратной связи</span></h2>
              <p className="text-foreground/70 mb-6">
                Заполните форму, и мы свяжемся с вами в ближайшее время
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    type="text"
                    label="Ваше имя"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    variant="bordered"
                    color="primary"
                    isRequired
                  />
                  <Input
                    type="email"
                    label="Email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    variant="bordered"
                    color="primary"
                    isRequired
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    type="tel"
                    label="Телефон"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    variant="bordered"
                    color="primary"
                  />
                  <Input
                    type="text"
                    label="Тема сообщения"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    variant="bordered"
                    color="primary"
                  />
                </div>
                <Textarea
                  label="Сообщение"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  variant="bordered"
                  color="primary"
                  minRows={5}
                  isRequired
                />
                <div>
                  <Button
                    type="submit"
                    color="primary"
                    size="lg"
                    isLoading={isSubmitting}
                    className="w-full md:w-auto"
                  >
                    {isSubmitting ? "Отправка..." : "Отправить сообщение"}
                  </Button>
                  
                  {submitSuccess && (
                    <motion.p 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-success mt-2"
                    >
                      Спасибо! Ваше сообщение успешно отправлено.
                    </motion.p>
                  )}
                </div>
              </form>
            </motion.div>
            
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="order-1 lg:order-2"
            >
              <h2 className="text-2xl md:text-3xl font-light mb-6">Контактная <span className="font-medium">информация</span></h2>
              <p className="text-foreground/70 mb-6">
                Вы можете связаться с нами по телефону или электронной почте. {/* или посетить наш офис в Москве */}
              </p>
              
              <div className="space-y-8 mb-8">
                {/* <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-full">
                    <Icon icon="lucide:map-pin" className="text-primary" width={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-medium mb-2">Адрес</h3>
                    <p className="text-foreground/70">г. Москва, ул. Арбат, 10, офис 205</p>
                    <p className="text-foreground/70 mt-1">Ближайшая станция метро: Арбатская</p>
                  </div>
                </div> */}
                
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-full">
                    <Icon icon="lucide:phone" className="text-primary" width={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-medium mb-2">Телефон</h3>
                    <p className="text-foreground/70">+7 (995) 881-02-47</p>
                    <p className="text-foreground/70 mt-1">Ежедневно с 10:00 до 21:00</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-full">
                    <Icon icon="lucide:mail" className="text-primary" width={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-medium mb-2">Email</h3>
                    <Link 
                      href="mailto:info@safonova-brand.ru" 
                      className="text-foreground/70 hover:text-foreground relative group overflow-hidden"
                    >
                      <p className="transition-colors duration-300 ease-out">info@safonova-brand.ru</p>
                      <span className="absolute left-0 bottom-0 w-0 h-px bg-foreground transition-all duration-300 ease-in-out group-hover:w-full"></span>
                    </Link>
                    <p className="text-foreground/70 mt-1">Ответим в течение 24 часов</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-full">
                    <Icon icon="lucide:clock" className="text-primary" width={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-medium mb-2">Часы работы</h3>
                    <p className="text-foreground/70">Понедельник - Пятница: 10:00 - 19:00</p>
                    <p className="text-foreground/70">Суббота: 11:00 - 17:00</p>
                    <p className="text-foreground/70">Воскресенье: Выходной</p>
                  </div>
                </div>
              </div>

            </motion.div>
          </div>
        </div>
      </section>

      {/* Stores Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-light mb-6">Торговые <span className="font-medium">точки</span></h2>
          <p className="text-foreground/70 mb-6">Вы можете приобрести наши товары в следующих магазинах:</p>

          <div className="p-6 md:p-8 bg-content2 border border-divider shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1">
                {storesError && <div className="text-danger">{storesError}</div>}
                {!stores && !storesError && <div className="text-foreground/60">Загрузка торговых точек...</div>}
                {stores && stores.length === 0 && <div className="text-foreground/60">Торговые точки не найдены</div>}
                {stores && (
                  <div className="space-y-3 divide-y divide-divider max-h-[520px] overflow-auto pr-2">
                    {stores.map((s) => {
                      const isSel = selectedStoreId && Number(selectedStoreId) === Number(s.id);
                      return (
                        <button
                          type="button"
                          key={s.id}
                          onClick={() => setSelectedStoreId(Number(s.id))}
                          className={`w-full text-left p-4 transition hover:bg-content3 ${isSel ? 'bg-primary/5 border-l-4 border-primary pl-3' : 'bg-transparent'}`}
                        >
                          <div className="font-medium">{s.name}</div>
                          <div className="text-foreground/70 text-sm mt-1">{s.address}</div>
                          <div className="text-foreground/70 text-sm mt-1">{s.phone || ''} {s.work_hours ? ` · ${s.work_hours}` : ''}</div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="lg:col-span-2">
                <StoresMap apiKey={yandexApiKey} stores={stores || []} selectedStoreId={selectedStoreId} onSelectStore={(id) => setSelectedStoreId(id)} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      {/* <section className="py-16 bg-content2">
        <div className="container mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-light mb-8 text-center">Как нас <span className="font-medium">найти</span></h2>
          
          <div className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
            <div className="w-full h-full bg-content3/50 flex items-center justify-center">
              <div className="text-center p-8">
                <div className="mb-4 mx-auto">
                  <Icon icon="lucide:map" className="w-16 h-16 text-primary/40" />
                </div>
                <h3 className="text-xl font-medium mb-2">Интерактивная карта</h3>
                <p className="text-foreground/70 mb-4">
                  Здесь будет отображаться карта с местоположением нашего офиса
                </p>
                <Button
                  color="primary"
                  variant="flat"
                  as="a" 
                  href="https://maps.google.com"
                  target="_blank" 
                  rel="noopener noreferrer"
                  endContent={<Icon icon="lucide:external-link" width={16} />}
                >
                  Открыть на Google Maps
                </Button>
              </div>
            </div>
          </div>
          
          <div className="mt-8 p-6 border border-divider bg-content1 text-center">
            <h3 className="text-xl font-medium mb-4">Приходите к нам в гости!</h3>
            <p className="text-foreground/80 max-w-2xl mx-auto">
              Мы будем рады видеть вас в нашем шоурме, где вы можете ознакомиться с полным ассортиментом продукции, получить консультацию и лично оценить качество нашей продукции.
            </p>
          </div>
        </div>
      </section> */}
    </>
  );
};